import { Check } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CanvasCommentRow } from "@/lib/api";
import { cn } from "@/lib/utils";

interface CanvasCommentMarkerProps {
	comment: CanvasCommentRow;
	zoom: number;
	/** Active (clicked) or hovered — shows the bubble and raises the marker. */
	isExpanded: boolean;
	onHoverChange: (commentId: number | null) => void;
	onMarkerClick: (commentId: number) => void;
	isEditing: boolean;
	editingText: string;
	onEditingTextChange: (value: string) => void;
	editInputRef: RefObject<HTMLInputElement | null>;
	onSaveEdit: () => void;
	onCancelEdit: () => void;
	onStartEdit: (comment: CanvasCommentRow) => void;
}

/** Comment pin on the canvas plus its hover / click bubble (with inline edit). */
export function CanvasCommentMarker({
	comment,
	zoom,
	isExpanded,
	onHoverChange,
	onMarkerClick,
	isEditing,
	editingText,
	onEditingTextChange,
	editInputRef,
	onSaveEdit,
	onCancelEdit,
	onStartEdit,
}: CanvasCommentMarkerProps) {
	// createdAt is an ISO string from the DB
	const now = new Date();
	const diffMs =
		now.getTime() - new Date(comment.createdAt).getTime();
	const diffSec = Math.floor(diffMs / 1000);
	const diffMin = Math.floor(diffSec / 60);
	const diffHr = Math.floor(diffMin / 60);
	const diffDay = Math.floor(diffHr / 24);
	let relativeTime = "just now";
	if (diffDay > 0)
		relativeTime = `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
	else if (diffHr > 0)
		relativeTime = `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
	else if (diffMin > 0) relativeTime = `${diffMin} min. ago`;

	return (
		<div
			className={cn("absolute", isExpanded ? "z-50" : "z-30")}
			style={{ left: comment.posX, top: comment.posY }}
			onMouseDown={(e) => e.stopPropagation()}
			onMouseEnter={() => onHoverChange(comment.id)}
			onMouseLeave={() => onHoverChange(null)}
		>
			{/* Marker dot */}
			<button
				onClick={() => onMarkerClick(comment.id)}
				className={cn(
					"relative w-8 h-8 rounded-full flex items-center justify-center text-2xs font-bold shadow-lg transition-all duration-200 hover:scale-110 bg-brand-400 text-text-on-brand",
					isExpanded
						? "ring-2 ring-brand-100 scale-110"
						: "hover:ring-2 hover:ring-brand-100",
				)}
				style={{
					transform: `scale(${1 / zoom})`,
					transformOrigin: "top left",
				}}
			>
				{(comment.userName ?? comment.userId)
					?.charAt(0)
					?.toUpperCase() || "?"}
			</button>

			{/* Comment bubble */}
			<AnimatePresence>
				{isExpanded && (
					<motion.div
						initial={{ opacity: 0, y: 6, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 6, scale: 0.95 }}
						transition={{ duration: 0.15 }}
						className="absolute top-9 left-0 min-w-[220px] max-w-[300px] z-40"
						style={{
							transform: `scale(${1 / zoom})`,
							transformOrigin: "top left",
						}}
					>
						<div className="bg-bg-sunken rounded-xl shadow-popover overflow-hidden px-3.5 py-3">
							<div className="flex items-start gap-2.5">
								{/* Avatar */}
								<div className="w-7 h-7 rounded-full bg-brand-400 flex items-center justify-center text-text-on-brand text-2xs font-semibold flex-shrink-0 mt-0.5">
									{(comment.userName ?? comment.userId)
										.charAt(0)
										.toUpperCase()}
								</div>

								<div className="flex-1 min-w-0">
									{/* userId + relative time */}
									<div className="flex items-center gap-2">
										<span className="text-sm font-semibold text-text-primary">
											{comment.userName ?? comment.userId}
										</span>
										<span className="text-2xs text-text-tertiary">
											{relativeTime}
										</span>
									</div>

									{/* Edit mode or display mode */}
									{isEditing ? (
										<form
											onSubmit={(e) => {
												e.preventDefault();
												onSaveEdit();
											}}
											className="flex items-center gap-1.5 mt-1.5"
										>
											<Input
												ref={editInputRef}
												type="text"
												value={editingText}
												onChange={(e) =>
													onEditingTextChange(e.target.value)
												}
												className="h-8 flex-1 px-2.5 py-1"
												onKeyDown={(e) => {
													if (e.key === "Escape") {
														onCancelEdit();
													}
												}}
											/>
											<Button
												type="submit"
												variant="secondary"
												size="icon-xs"
												disabled={!editingText.trim()}
											>
												<Check size={12} strokeWidth={2.5} />
											</Button>
										</form>
									) : (
										<p
											className="text-sm text-text-secondary leading-relaxed mt-0.5 break-words cursor-pointer hover:text-text-primary transition-colors"
											onClick={() => onStartEdit(comment)}
										>
											{comment.content}
										</p>
									)}
								</div>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}
